import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { AppAndAddonActivationStatus } from '@vendasta/accounts/legacy';
import { BillingUiModule } from '@vendasta/billing-ui';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { IconComponent, VaIconModule } from '@vendasta/uikit';
import { ActivatableItem, ActivationStatus, Edition, SelectedItem } from '../../../../../activation/core/interface';
import { isAppActive } from '../../../../../activation/core/utils';
import { AppIDs } from '../../../../../constants';
import { GoogleWorkspaceSeatAddonIds } from '../../../../../marketplace-app/global-brands-constants';

interface CheckboxClickEvent {
  appId: string;
  currentQuantity: number;
  allowMultiActivations: boolean;
}

interface QuantityChangeEvent {
  appId: string;
  newQuantity: number;
  allowMultiActivations: boolean;
}

interface EditionChangeEvent {
  edition: Edition;
  appId: string;
  currentQuantity: number;
}

@Component({
  standalone: true,
  imports: [
    NgClass,
    MatCheckboxModule,
    VaIconModule,
    MatSelectModule,
    TranslateModule,
    FormsModule,
    MatInputModule,
    BillingUiModule,
    MatIconModule,
    IconComponent,
    NgIf,
  ],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-activatable-row]',
  templateUrl: './activatable-row.component.html',
  styleUrls: ['./activatable-row.component.scss'],
  // This page is using OnPush change Detection do not remove
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivatableRowComponent implements OnInit {
  @Input() quantity = 0;
  @Input() indented = false;
  @Input() hidePricing: boolean;
  @Input() removableItems?: boolean = false;
  @Input() features: any = [];

  @Output() itemSelectionChanged: EventEmitter<SelectedItem> = new EventEmitter();
  @Output() itemDeleteClicked: EventEmitter<string> = new EventEmitter();
  item: ActivatableItem;
  dynamicClass: { [cls: string]: boolean } = {};
  appCanBeSelected: boolean;
  isActive: boolean;
  activeCount: number;
  isPending: boolean;
  isCancelled: boolean;
  cancelledStatus = ActivationStatus.CANCELED;

  usesEditions: boolean;
  currentlySelectedEdition: Edition;
  filteredEditions: Edition[] = [];

  listingBuilderProduct = AppIDs.ListingBuilder;

  @Input()
  set currentlySelectedEditionId(editionId: string) {
    if (this.item && this.item.editionData && this.item.editionData.editions) {
      const selectedEdition = this.item.editionData.editions.find((edition) => edition.editionId === editionId);
      this.currentlySelectedEdition = selectedEdition || this.item.editionData.editions[0];
    } else {
      this.currentlySelectedEdition = null;
    }
  }

  @Input()
  set activatableItem(item: ActivatableItem) {
    this.dynamicClass = {
      disabled: Boolean(item && item.restrictedReason),
    };
    this.item = item;
    if (this.item && this.item.pricing) {
      this.item.pricing.isProratedToParent = GoogleWorkspaceSeatAddonIds.some((addonId) => addonId === item.appId);
    }

    if (!item) {
      return;
    }
  }

  constructor(private analyticsService: ProductAnalyticsService) {}

  ngOnInit(): void {
    const item = this.item;

    const activeStatus = (item.activeState || []).find((s) => s && s.status === AppAndAddonActivationStatus.ACTIVATED);
    if (activeStatus) {
      this.isActive = true;
      this.activeCount = activeStatus.count;
    } else {
      this.isActive = false;
      this.activeCount = 0;
    }

    this.usesEditions = item.editionData && item.editionData.usesEditions;
    if (this.usesEditions && item.editionData.editions.length > 0) {
      if (activeStatus) {
        this.currentlySelectedEdition = item.editionData.editions.find(
          (edition) => edition.editionId === (item.editionId || ''),
        );
      } else if (!this.currentlySelectedEdition) {
        if (this.currentlySelectedEditionId) {
          const selectedEdition = item.editionData.editions.find(
            (edition) => edition.editionId === this.currentlySelectedEditionId,
          );
          this.currentlySelectedEdition = selectedEdition || item.editionData.editions[0];
        } else {
          this.currentlySelectedEdition = item.editionData.editions[0];
        }
      }
      this.filteredEditions = item.editionData.editions;
    } else {
      this.currentlySelectedEdition = null;
    }

    this.isPending = Boolean(
      (item.activeState || []).find((s) => s && s.status === AppAndAddonActivationStatus.PENDING),
    );
    this.isCancelled = Boolean(
      (item.activeState || []).find((s) => s && s.status === AppAndAddonActivationStatus.CANCELED),
    );
    const isActive = isAppActive(item.appId, item.activeState, item.isTrial);
    this.appCanBeSelected = (!isActive || item.allowMultipleActivations) && !item.isActiveAccount;
  }

  onItemQuantityChanged({ appId, newQuantity, allowMultiActivations }: QuantityChangeEvent): void {
    newQuantity = Number(newQuantity || 0); // Input gives us a string
    if (isNaN(newQuantity)) {
      // conversion from string failed (they somehow typed garbage in the input field)
      newQuantity = 0;
    }
    if (newQuantity > 1 && !allowMultiActivations) {
      newQuantity = 1;
    }
    this.itemSelectionChanged.emit({ appId: appId, quantity: newQuantity });
  }

  onCheckboxChanged(
    event: MatCheckboxChange,
    { appId, currentQuantity, allowMultiActivations }: CheckboxClickEvent,
  ): void {
    this.analyticsService.trackEvent('clicked-product-checkbox', '', 'click');
    const checked = event.checked;
    currentQuantity = currentQuantity || 0;

    if (!checked) {
      this.itemSelectionChanged.emit({ appId: appId, quantity: 0 });
    } else {
      const selectedItem = {
        appId: appId,
        quantity: allowMultiActivations || currentQuantity === 0 ? currentQuantity + 1 : currentQuantity,
        editionId: this.currentlySelectedEdition?.editionId || null,
      };
      this.itemSelectionChanged.emit(selectedItem);
    }
  }

  onEditionSelectionChange($event: EditionChangeEvent): void {
    if ($event.currentQuantity > 0) {
      this.itemSelectionChanged.emit({
        appId: $event.appId,
        quantity: 1,
        editionId: $event.edition ? $event.edition.editionId : null,
      });
    }
  }
}
